import { useMemo, useCallback } from 'react';
import { Col, Row, Card, Button, ButtonGroup, ButtonToolbar } from 'react-bootstrap';
import { useQuery, useMutation, NetworkStatus } from '@apollo/client';
import { useSelector, useDispatch } from 'react-redux';
import { validate as uuidValidate } from 'uuid';
import { RefreshCw as RefreshCwIcon, Table as TableIcon } from 'react-feather';

import LinkContainer from '../components/link_container';
import {
  renderOverlay,
  renderOffline,
  renderError,
  renderDateString,
} from '../components/render_helpers';
import ReactTable from '../components/react_table/react_table';
import { toastSuccess, toastWarning, toastError } from '../lib/toast_helpers';
import {
  consignmentListPageQuery,
  consignmentDelete as consignmentDeleteMutation,
} from '../graphql/consignment_queries';
import { settingsSet, settingsReset } from '../store/settings_slice';
import * as updateFunctions from '../update_functions';

const ConsignmentList = () => {
  const dispatch = useDispatch();
  const currentUser = useSelector((state) => state.auth.user);
  const settingsMutating = useSelector((state) => state.settings.mutating);
  const settingsOnline = useSelector((state) => state.settings.online);
  const settingsTenant = useSelector((state) => state.settings.tenant);
  const [consignmentDelete] = useMutation(consignmentDeleteMutation);

  const tableStateKey = 'consignment';

  const {
    data: pageData,
    loading: pageLoading,
    error: pageError,
    refetch: pageRefetch,
    networkStatus: pageNetworkStatus,
    client: pageClient,
  } = useQuery(consignmentListPageQuery, {
    notifyOnNetworkStatusChange: true,
    onCompleted: ({ consignmentList }) => {
      // write an individual query for every consignment in the list
      updateFunctions.consignmentList(pageClient, consignmentList);
    },
  });

  const pageLoadedOrRefetching = useMemo(
    () => !pageLoading || (pageLoading && pageNetworkStatus === NetworkStatus.refetch),
    [pageLoading, pageNetworkStatus]
  );

  const tableResetClicked = useCallback(() => {
    dispatch(settingsReset(['tableState', tableStateKey]));
  }, [tableStateKey, dispatch]);

  const perms = useMemo(() => {
    if (currentUser?.id) {
      return currentUser.perms;
    }
    return {};
  }, [currentUser]);

  const consignmentDeleteClicked = async (e) => {
    const dataId = e.currentTarget.getAttribute('data-id');
    const consignmentId = uuidValidate(dataId)
      ? dataId
      : parseInt(e.currentTarget.getAttribute('data-id'), 10);
    const mutationData = {
      variables: { id: consignmentId },
      context: {
        serializationKey: settingsTenant,
        tracked: true,
        recordType: 'ConsignmentType',
        recordId: consignmentId,
        mutationType: 'DELETE',
      },
      update: updateFunctions.consignmentDelete,
    };
    mutationData.optimisticResponse = updateFunctions.optimistic(
      'consignmentDelete',
      mutationData
    );
    dispatch(
      settingsSet({
        mutating: true,
      })
    );
    if (settingsOnline) {
      try {
        await consignmentDelete(mutationData);
        toastSuccess('Consignment delete ok');
      } catch (err) {
        console.log(err.toString());
        toastError('Consignment delete failed');
      } finally {
        dispatch(
          settingsSet({
            mutating: false,
          })
        );
      }
    } else {
      consignmentDelete(mutationData);
      toastWarning(`Consignment delete ok locally. Go online to make permanent`);
      dispatch(
        settingsSet({
          mutating: false,
        })
      );
    }
  };

  const getSiteName = (row) => {
    const {
      site: { name: siteName },
    } = row;
    return siteName;
  };

  const getSupplierName = (row) => {
    const {
      supplier: { name: supplierName },
    } = row;
    return supplierName;
  };

  const parentColumns = [
    {
      header: 'Site',
      id: 'site',
      accessorFn: (row) => getSiteName(row),
      filterType: 'dropdown',
    },
    {
      header: 'Supplier',
      id: 'supplier',
      accessorFn: (row) => getSupplierName(row),
      filterType: 'dropdown',
    },
    // {
    //   header: 'Container Identifier',
    //   accessorKey: 'containerIdentifier',
    //   enableColumnFilter: true,
    //   filterFn: 'includesString',
    // },
    {
      header: 'Shipment Number',
      accessorKey: 'shipmentNumber',
      filterType: 'dropdown',
    },
    {
      header: 'Container Reference',
      accessorKey: 'containerReference',
      enableColumnFilter: true,
      filterFn: 'includesString',
    },
    {
      header: 'Container Number',
      accessorKey: 'containerNumber',
      enableColumnFilter: true,
      filterFn: 'includesString',
    },
    {
      header: 'Container Seal Number',
      accessorKey: 'containerSealNumber',
      enableColumnFilter: true,
      filterFn: 'includesString',
    },
    {
      header: 'Estimated Delivery',
      accessorKey: 'estimatedDeliveryAt',
      enableColumnFilter: false,
      accessorFn: ({ estimatedDeliveryAt }) => renderDateString(estimatedDeliveryAt),
    },
  ];

  const renderContent = () => (
    <>
      <Row className="mt-4 mb-3">
        <Col>
          <h1 className="h3 mb-3">Consignments</h1>
        </Col>
        <Col className="noprint" sm="auto">
          <ButtonToolbar>
            {perms.administrator && (
              <ButtonGroup className="me-2">
                <LinkContainer to="/consignments/new">
                  <Button variant="primary">New Consignment</Button>
                </LinkContainer>
              </ButtonGroup>
            )}
            <ButtonGroup className="me-0">
              <Button title="Reset table filters and sorting" onClick={tableResetClicked}>
                <TableIcon />
              </Button>
              <Button
                title="Refresh data"
                onClick={() => pageRefetch()}
                disabled={!settingsOnline}
              >
                <RefreshCwIcon />
              </Button>
            </ButtonGroup>
          </ButtonToolbar>
        </Col>
      </Row>
      <Row>
        <Col>
          <Card>
            <Card.Body>
              <ReactTable
                rootName={tableStateKey}
                parentColumns={parentColumns}
                data={pageData.consignmentList}
                doEdit={perms.administrator}
                doDelete={currentUser?.perms?.administrator}
                handleDelete={consignmentDeleteClicked}
                hideResetTable
              />
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </>
  );

  return (
    <div>
      {renderOverlay(pageLoading, settingsMutating, settingsOnline)}
      {renderOffline(settingsOnline)}
      {renderError(pageError)}
      {!pageError && pageLoadedOrRefetching && renderContent()}
    </div>
  );
};

export default ConsignmentList;
